import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ApiError } from '@/lib/api-error';
import { isTokenExpired } from '@/lib/jwt';
import { ROUTES } from '@/routes/paths';
import { selectToken, useAuthStore } from '@/stores/auth-store';
import { FullPageLoader } from '@/components/ui/Spinner';
import { SessionErrorScreen } from './SessionErrorScreen';
import { useMe } from './use-me';

/**
 * Guarda de las rutas protegidas.
 *
 * Flujo:
 *  1. Sin token (o con el `exp` del JWT ya vencido) → redirect a `/login`, guardando a
 *     dónde quería ir para volver después de autenticarse.
 *  2. Con token válido → `GET /api/auth/me` (TanStack Query). Mientras responde, pantalla
 *     de carga (evita el "flash" de datos vacíos).
 *  3. Si `me` responde 401, el cliente HTTP ya limpió la sesión; aquí solo se remata el
 *     redirect a `/login`. Otros errores (backend caído) muestran una pantalla con
 *     reintentar / cerrar sesión en lugar de dejar al usuario en blanco.
 */
export function RequireAuth() {
  const token = useAuthStore(selectToken);
  const location = useLocation();
  const meQuery = useMe();

  const hasValidToken = Boolean(token) && !isTokenExpired(token);

  if (!hasValidToken) {
    return (
      <Navigate
        to={ROUTES.login}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (meQuery.isPending) {
    return <FullPageLoader label="Cargando tu sesión…" />;
  }

  if (meQuery.isError) {
    if (meQuery.error instanceof ApiError && meQuery.error.isUnauthorized) {
      return <Navigate to={ROUTES.login} replace />;
    }

    return <SessionErrorScreen error={meQuery.error} onRetry={() => void meQuery.refetch()} />;
  }

  return <Outlet />;
}
